import {
  type DefectDetail,
  type DefectPhoto,
  type DefectSummary,
  REPAIR_ACTION_LABELS,
  REPAIR_STATUS_LABELS,
  type RepairStatus,
} from '@sivan/shared';
import { Icon } from '../ui/Icon';

// The shared pieces of ניהול ליקויים and the supplier portal (DR-196). A status always shows its
// words and an icon, never colour alone.

const STATUS_ICON: Record<RepairStatus, 'circle' | 'clock' | 'refresh' | 'circle-check'> = {
  open: 'circle',
  in_progress: 'clock',
  fixed: 'refresh',
  verified: 'circle-check',
};

/** A saved finding Sivan has not released to a contractor yet. */
export function NotReleasedBadge() {
  return (
    <span className="repair-status repair-status--unreleased">
      <Icon name="lock" />
      טרם שוחרר לקבלן
    </span>
  );
}

export function DefectStatus({ defect }: { readonly defect: DefectSummary }) {
  return (
    <>
      {defect.released || defect.status === 'verified' ? (
        <RepairStatusBadge status={defect.status} />
      ) : (
        <NotReleasedBadge />
      )}
      {defect.handoverStatus === 'draft' && <span className="defect-chip">מסירה בטיוטה</span>}
    </>
  );
}

export function RepairStatusBadge({ status }: { readonly status: RepairStatus }) {
  return (
    <span className={`repair-status repair-status--${status}`}>
      <Icon name={STATUS_ICON[status]} />
      {REPAIR_STATUS_LABELS[status]}
    </span>
  );
}

export const photoSrc = (photo: DefectPhoto): string => `/api${photo.url}`;

const date = (value: string) => {
  const [y, m, d] = value.split('-');
  return `${d ?? ''}/${m ?? ''}/${y ?? ''}`;
};
export const formatDate = (value: string | null): string => (value === null ? '—' : date(value));

/** One defect as a card: where, what, who, its state, one obvious next step. */
export function DefectCard({
  defect,
  href,
  showProject = false,
  action,
}: {
  readonly defect: DefectSummary;
  readonly href: string;
  readonly showProject?: boolean;
  readonly action: string;
}) {
  return (
    <li className="defect-card">
      <a
        className="defect-card__link"
        href={href}
        aria-label={`${defect.apartment === '' ? '' : `דירה ${defect.apartment} · `}${defect.area} · ${defect.component}: ${defect.released || defect.status === 'verified' ? REPAIR_STATUS_LABELS[defect.status] : 'טרם שוחרר לקבלן'}`}
      >
        <span className="defect-card__photo" aria-hidden="true">
          {defect.photo === null ? (
            <Icon name="camera" />
          ) : (
            <img src={photoSrc(defect.photo)} alt="" loading="lazy" />
          )}
        </span>
        <span className="defect-card__body">
          <span className="defect-card__where">
            {showProject && `${defect.projectName} · `}
            {defect.building} · דירה {defect.apartment}
          </span>
          <b className="defect-card__what">
            {defect.area} · {defect.component}
          </b>
          <span className="defect-card__text">{defect.description}</span>
          <span className="defect-card__meta">
            <DefectStatus defect={defect} />
            {defect.workCategory !== null && (
              <span className="defect-chip">{defect.workCategory}</span>
            )}
            {defect.contractor !== null && !showProject && (
              <span className="defect-chip">{defect.contractor.name}</span>
            )}
            {defect.dueOn !== null && (
              <span className={`defect-chip${defect.overdue ? ' defect-chip--late' : ''}`}>
                {defect.overdue && <Icon name="alert" />}
                {defect.overdue ? 'באיחור · ' : 'יעד '}
                {formatDate(defect.dueOn)}
              </span>
            )}
          </span>
        </span>
        <span className="defect-card__go">
          {action}
          <Icon name="chevron-left" />
        </span>
      </a>
    </li>
  );
}

export function PhotoStrip({
  photos,
  label,
}: {
  readonly photos: readonly DefectPhoto[];
  readonly label: string;
}) {
  if (photos.length === 0) return <p className="hint">אין תמונות.</p>;
  return (
    <ul className="defect-photos" aria-label={label}>
      {photos.map((p, i) => (
        <li key={p.id}>
          <a
            href={photoSrc(p)}
            target="_blank"
            rel="noreferrer"
            aria-label={`${label}: תמונה ${String(i + 1)}`}
          >
            <img src={photoSrc(p)} alt="" loading="lazy" />
          </a>
        </li>
      ))}
    </ul>
  );
}

/** The repair's history: who did what, when, with their photos and notes. */
export function RepairHistory({ history }: { readonly history: DefectDetail['history'] }) {
  if (history.length === 0) return <p className="hint">עדיין לא בוצעו פעולות בליקוי.</p>;
  return (
    <ol className="repair-history">
      {history.map((e) => (
        <li key={e.id} className={`repair-history__item repair-history__item--${e.action}`}>
          <span className="repair-history__head">
            <b>{REPAIR_ACTION_LABELS[e.action]}</b>
            <span className="hint">
              {new Date(e.occurredAt).toLocaleString('he-IL', {
                dateStyle: 'short',
                timeStyle: 'short',
              })}
            </span>
          </span>
          <span className="hint">
            {e.actor}
            {e.actorSupplier !== null && ` · ${e.actorSupplier}`}
            {e.action === 'assigned' && e.contractor !== null && ` · לקבלן: ${e.contractor}`}
            {e.action === 'assigned' && e.dueOn !== null && ` · יעד ${formatDate(e.dueOn)}`}
          </span>
          {e.note !== null && <p className="repair-history__note">{e.note}</p>}
          {e.photos.length > 0 && <PhotoStrip photos={e.photos} label="תמונות התיקון" />}
        </li>
      ))}
    </ol>
  );
}
